import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Download, ShieldPlus } from "lucide-react";
import { PageHero } from "./About";
import { QuickFactsCard } from "./campus-life/CampusLifePageParts";

const bloodDonationReport =
  "/Clubs/Red-Ribbon-Red-Cross/blood-donation-camp-16-03-2017.pdf";
const engineersDayBloodDonationReport =
  "/Clubs/Red-Ribbon-Red-Cross/blood-donation-camp-engineers-day-18-09-2024.pdf";
const appreciationCertificate =
  "/Clubs/Red-Ribbon-Red-Cross/certificate-of-appreciation-principal-vdit.pdf";
const addictionAwarenessReport =
  "/Clubs/Red-Ribbon-Red-Cross/awareness-program-addiction-risks-law-19-05-2026.pdf";
const cancerTherapyReport =
  "/Clubs/Red-Ribbon-Red-Cross/karnataak-cancer-therapy-research-institute-08-09-2025.pdf";
const redCrossAffiliationCertificate =
  "/Clubs/Red-Ribbon-Red-Cross/red-cross-certificate-of-affiliation.pdf";
const worldRedCrossDayReport =
  "/Clubs/Red-Ribbon-Red-Cross/world-red-cross-day-05-05-2026.pdf";

const activityRows = [
  ["01", "Blood Donation Camp", "16-03-2017"],
  ["02", "Blood Donation Camp on occasion of Engineers Day", "18-09-2024"],
  ["03", "Karnataak Cancer Therapy & Research Institute Programme", "08-09-2025"],
  ["04", "World Red Cross Day Awareness Programme", "05-05-2026"],
  ["05", "Awareness Program on Addiction: the Risks & the Law", "19-05-2026"],
  ["06", "First Aid Awareness Programme", "To be updated"],
  ["07", "Community Health Activity", "To be updated"],
  ["08", "Humanitarian Service Initiative", "To be updated"],
];

const activities = [
  {
    title: "Blood Donation Camp (16-03-2017)",
    description:
      "Voluntary blood donation initiative conducted to promote humanitarian service and healthcare support.",
    report: bloodDonationReport,
  },
  {
    title: "Blood Donation Camp on occasion of Engineers Day (18-09-2024)",
    description:
      "Voluntary blood donation camp organized on the occasion of Engineers Day to encourage service and community healthcare support.",
    report: engineersDayBloodDonationReport,
  },
  {
    title: "Certificate of Appreciation to Principal of VDIT",
    description:
      "Certificate of appreciation recognizing the institution's support and contribution to service activities.",
    report: appreciationCertificate,
  },
  {
    title: "Red Cross Certificate of Affiliation",
    description:
      "Affiliation certificate recognizing the Red Cross unit and its association with Red Cross service activities.",
    report: redCrossAffiliationCertificate,
  },
  {
    title: "Karnataak Cancer Therapy & Research Institute Programme (08-09-2025)",
    description:
      "Awareness programme connected with cancer therapy and research, encouraging health education and preventive care.",
    report: cancerTherapyReport,
  },
  {
    title: "Awareness Program on Addiction: the Risks & the Law (19-05-2026)",
    description:
      "Awareness programme focused on addiction risks, legal consequences, and responsible choices among students.",
    report: addictionAwarenessReport,
  },
  {
    title: "World Red Cross Day Awareness Programme (05-05-2026)",
    description:
      "Awareness programme promoting humanitarian values and community service.",
    report: worldRedCrossDayReport,
  },
  {
    title: "First Aid Awareness Programme",
    description:
      "Training sessions to improve emergency preparedness and first aid awareness.",
    report: "#",
  },
  {
    title: "Community Health Activity",
    description:
      "Health-focused outreach programme conducted for community welfare.",
    report: "#",
  },
  {
    title: "Humanitarian Service Initiative",
    description:
      "Student-led service activity supporting social and humanitarian causes.",
    report: "#",
  },
];

const quickFacts = [
  ["Category", "Student Clubs"],
  ["Club", "Red Cross Club"],
  ["Focus", "Humanitarian Service"],
  ["Activity Year", "2025-26"],
  ["Activities", "4+"],
  ["Reports", "To be updated"],
  ["First Aid", "Yes"],
  ["Community Service", "Active"],
];

const objectives = [
  "To conduct social and health awareness programmes and promote health consciousness among youth.",
  "To create awareness on the care of one's own health and that of others.",
  "To encourage students to extend humanitarian service to society.",
  "To provide First Aid training and awareness to all YRC volunteers.",
  "To develop leadership, compassion, and a sense of social responsibility among the youth.",
  "To promote voluntary service, national integration, and community welfare activities.",
];

const roles = [
  "Uphold and promote the humanitarian principles of the Indian Red Cross Society among students and the community.",
  "Develop awareness about personal and public health, hygiene, and safety among youth.",
  "Encourage and organize voluntary service activities including blood donation, first-aid training, disaster relief support, and community welfare efforts.",
  "Form student volunteers to participate in service-oriented activities.",
];

const RedCrossClub = () => (
  <main>
    <PageHero
      title="Red Cross Club"
      sub="Humanitarian service, health awareness and community welfare"
      image="https://images.unsplash.com/photo-1584515933487-779824d29309?w=2400&q=80"
    />

    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
      <Link
        to="/clubs"
        className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:underline mb-8"
      >
        <ArrowLeft size={16} />
        Back to Clubs
      </Link>

      <div className="grid lg:grid-cols-3 gap-8">
        <section className="lg:col-span-2 bg-white border border-brand/15 p-7 md:p-9">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 border border-brand/20 flex items-center justify-center text-brand">
              <ShieldPlus size={22} />
            </div>

            <p className="text-xs uppercase tracking-[0.24em] text-brand font-semibold">
              Humanitarian Club
            </p>
          </div>

          <h2
            className="text-3xl md:text-5xl text-brand font-semibold"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Red Cross Club Activities
          </h2>

          <p className="mt-5 text-[16px] text-[#2a2a2a]/85 leading-relaxed">
            Red Ribbon Club initiatives are major Indian campaigns aimed at raising HIV/AIDS awareness, reducing stigma, and promoting healthy behaviours among youth and vulnerable populations. These initiatives, including the largest human red ribbon chain, use education and counselling to fight stigma, encourage testing, and empower communities.
          </p>

        </section>

        <aside className="bg-white border border-brand/15 p-5">
          <QuickFactsCard facts={quickFacts} />
        </aside>
      </div>

      <section className="mt-12 grid lg:grid-cols-2 gap-6">
        <article className="bg-white border border-brand/15 p-6">
          <h3
            className="text-2xl text-brand font-semibold mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Objectives
          </h3>
          <ul className="space-y-3 text-[15px] text-[#2a2a2a]/85">
            {objectives.map((objective) => (
              <li key={objective} className="flex gap-3">
                <span className="text-brand font-semibold">•</span>
                <span>{objective}</span>
              </li>
            ))}
          </ul>
        </article>

        <article className="bg-white border border-brand/15 p-6">
          <h3
            className="text-2xl text-brand font-semibold mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Roles and Responsibilities
          </h3>
          <ul className="space-y-3 text-[15px] text-[#2a2a2a]/85">
            {roles.map((role) => (
              <li key={role} className="flex gap-3">
                <span className="text-brand font-semibold">•</span>
                <span>{role}</span>
              </li>
            ))}
          </ul>
        </article>
      </section>

      <section className="mt-12">
        <div className="bg-white border border-brand/15 overflow-hidden">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="border p-3 text-left">Sl. No</th>
                <th className="border p-3 text-left">Activity</th>
                <th className="border p-3 text-left">Date</th>
              </tr>
            </thead>
            <tbody>
              {activityRows.map(([id, title, date]) => (
                <tr key={id}>
                  <td className="border p-3">{id}</td>
                  <td className="border p-3">{title}</td>
                  <td className="border p-3">{date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-12">
        <div className="grid md:grid-cols-2 gap-5">
          {activities.map((activity) => (
            <article
              key={activity.title}
              className="bg-white border border-brand/15 p-5"
            >
              <div className="flex gap-3">
                <Calendar size={18} />
                <div>
                  <h3 className="text-xl text-brand font-semibold">
                    {activity.title}
                  </h3>

                  <p className="mt-2 text-sm text-[#2a2a2a]/85">
                    {activity.description}
                  </p>

                  <a
                    href={activity.report}
                    className="inline-flex items-center gap-2 mt-4 bg-brand text-white px-4 py-2 text-xs font-semibold"
                  >
                    <Download size={14} />
                    View Report
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </section>
  </main>
);

export default RedCrossClub;